import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Users, Star, Calendar } from "lucide-react";

const VenuesPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVenues();
  }, []);

  const fetchVenues = async () => {
    try {
      const response = await axios.get(`${API}/venues`);
      setVenues(response.data);
    } catch (error) {
      console.error("Error fetching venues:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 dark:bg-dark-bg dark:from-dark-bg dark:to-dark-bg transition-colors duration-300">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 dark:bg-dark-bg dark:from-dark-bg dark:to-dark-bg transition-colors duration-300" data-testid="venues-page">
      <Navbar user={user} onLogout={onLogout} />
      
      <main className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">
            Halı Sahalar
          </h1>
          <p className="text-lg text-gray-600">En iyi halı sahaları keşfet ve rezervasyon yap</p>
        </div>

        {/* Venues Grid */}
        {venues.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-gray-500 text-lg">Henüz saha bulunmuyor</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {venues.map((venue) => (
              <Card 
                key={venue.id} 
                className="overflow-hidden hover-lift cursor-pointer group"
                onClick={() => navigate(`/venues/${venue.id}`)}
                data-testid={`venue-card-${venue.id}`}
              >
                <div className="h-48 overflow-hidden bg-gradient-to-br from-emerald-100 to-green-200 relative">
                  {venue.images && venue.images.length > 0 ? (
                    <img 
                      src={venue.images[0]} 
                      alt={venue.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Calendar className="h-16 w-16 text-emerald-600 opacity-50" />
                    </div>
                  )}
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-white text-emerald-600 shadow-lg">
                      <Star className="h-3 w-3 mr-1 fill-current" />
                      {venue.rating}
                    </Badge>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl">{venue.name}</CardTitle>
                  <CardDescription className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    {venue.location}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Users className="h-4 w-4" />
                        <span className="text-sm">Kapasite: {venue.capacity} kişi</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t">
                      <span className="text-2xl font-bold text-emerald-600">{venue.pricePerHour} TL/saat</span>
                      <Button 
                        size="sm" 
                        className="bg-gradient-to-r from-emerald-500 to-green-600 dark:from-dark-card dark:to-dark-accent"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/venues/${venue.id}`);
                        }}
                      >
                        Rezerve Et
                      </Button>
                    </div>
                    {venue.amenities && venue.amenities.length > 0 && (
                      <div className="flex flex-wrap gap-1 pt-2">
                        {venue.amenities.slice(0, 3).map((amenity, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {amenity}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default VenuesPage;